import React, {useContext, useEffect} from "react";
import {Formik} from "formik";
import {QuotationContext} from "../../../contexts/QuotationContext";
import {MtplQuotation, Quotation} from "../../../model/quotation";
import {OutputValue} from "../../../components/inputs/OutputValue";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPen} from "@fortawesome/free-solid-svg-icons"
import {OutputLink} from "../../../components/OutputLink";
import {StepContext} from "../../../contexts/StepContext";
import {VerificationComponent} from "../other/VerificationComponent";
import {AdditionalCovarageCascoMessage} from "../../../components/InfoMessage/AdditionalCovatrageCascoMessage";
import {AdditionalCovarageVtplMessage} from "../../../components/InfoMessage/AdditionalCovarageVtplMessage";
import {OutputValueFieldInfo} from "../../../components/inputs/OutputValue/OutputValueFieldInfo"
import "./MtplVerificationFormStyles.scss";
import {dateFormat, premiumFormat, sumInsuredFormat} from "../../../utils/format";
import { formatVehicleEngineType, isSmartDrive } from "../../../utils/quotation";
import {strings} from "../../../strings/strings";
import * as log from "../../../utils/log";
import {LogFunctionalModule} from "../../../utils/log";
import {ProductType} from "../../../api/model/common";
import { useHotjar } from "../../../utils/hooks/useHotjar";
import { EventTypeKey, useTabCloseTracking } from "./hooks/useTabCloseTracking";
import useSwitchToNewContent from "../../../utils/hooks/useSwitchToNewContent";
import { InstallmentPayment } from "../../../components/InstallmentPayment/InstallmentPayment";

interface MtplVerificationForm {
    onVerify: (quotation: Quotation, paymentPageUrl: string) => void;
}

export const MtplVerificationForm = ({onVerify}: MtplVerificationForm) => {
    const {quotation, isQuotationEditable, tarrificationProgram, type} = useContext<QuotationContext<MtplQuotation>>(QuotationContext);
    const {backStepListener} = useContext(StepContext);

    const {trackHotjar} = useHotjar()

    useEffect(() => {
        let event = isSmartDrive(tarrificationProgram) ? 'osago_emtpl_smart_information' : 'osago_information';
        console.log('Google Analytics Event = ' + event);
        // @ts-ignore
        window.dataLayer.push({'event': event});

        trackHotjar("confirm-order", type)
    }, []);

    const showNewContent = useSwitchToNewContent();

    const primaryInsuredFullName = getPrimaryInsuredFullName();
    const coverageText = `${strings.packageOptionMtplName} ${strings.franchise3 + sumInsuredFormat(quotation.mtplContract.deductible)} ${strings.currencyUAH}`;

    function getPrimaryInsuredFullName() {
        let result = quotation.mtplContract.primaryInsured.surname + ' ' + quotation.mtplContract.primaryInsured.givenName;
        if (quotation.mtplContract.primaryInsured.middleName !== null && quotation.mtplContract.primaryInsured.middleName !== '') {
            result = result + ' ' + quotation.mtplContract.primaryInsured.middleName;
        }
        return result;
    }

    const onBackStepClick = (quotation) => {
        const date = new Date();
        log.sendLog(date, LogFunctionalModule.STORE_EMTPL, 'INFO',"VERIFICATION_FORM_NEXT_HANDLER",
            [{ quotation: quotation }], '')
        backStepListener(quotation)
    }

    const cascoAdditionalCoveragePremiumFirstPayment = quotation.cascoAdditionalCoverage.premium;
    const totalPremium = isSmartDrive(tarrificationProgram)
        ? `${premiumFormat(
            quotation.mtplContract.premium
            + quotation.vtplAdditionalCoverage.premium
            + cascoAdditionalCoveragePremiumFirstPayment
        )} ${strings.currencyUAH}`
        : `${premiumFormat(quotation.totalPremium)} ${strings.currencyUAH}`;

    const { email } = quotation?.mtplContract?.primaryInsured || {};
    useTabCloseTracking(email, EventTypeKey.AbandonedOscpv);

    return (
        <div className="verification-form fluid container-fluid">
            <div className="verification-form__info">
                <Formik initialValues={{
                    coverage: coverageText,
                    expiration: dateFormat(quotation.mtplContract.startDate) + '-' + dateFormat(quotation.mtplContract.endDate),
                    primaryInsuredFullName: primaryInsuredFullName,
                    totalPremium: totalPremium,
                    quotation
                }}
                        onSubmit={values => {
                        }}>
                    {formProps => (
                        <form onSubmit={formProps.handleSubmit} className="form">
                            <div className="verification-form__info__general">
                                <div style={{gridArea: "title"}}>
                                    <h1>{strings.scenarioHeaderName}</h1>
                                </div>

                                <div style={{gridArea: "total"}}>
                                    <OutputValue name={'totalPremium'}
                                                 label={strings.price}/>
                                    <div className={"installment-block"}>
                                        <span className={"sales-package__installment-alt"}>
                                            {"або"}
                                        </span>
                                        <InstallmentPayment size={"medium"}
                                                            installmentCount={3}
                                                            fullAmount={quotation.totalPremium} />
                                    </div>
                                </div>

                                <div style={{gridArea: "coverage"}}>
                                    <OutputValue name={'coverage'}
                                                 label={strings.coverage}/>
                                </div>

                                <div style={{gridArea: "expiration"}}>
                                    <OutputValue name={'expiration'} label={strings.expiration}/>
                                </div>


                                <div style={{gridArea: "additional"}}>
                                    <div className={'output-value'}>
                                        <span className={'output-value__label'}>{strings.additional}</span>
                                        <div>
                                            {quotation.cascoAdditionalCoverage.covered === true && (
                                                <OutputValueFieldInfo id={'AdditionalCovarageCasco'}
                                                                      name={'AdditionalCovarageCasco'}
                                                                      outputValue={
                                                                          isSmartDrive(tarrificationProgram) ?
                                                                              strings.packageOptionCascoNameSmartDrive
                                                                              :
                                                                              strings.packageOptionCascoName
                                                                      }
                                                                      messageInfoObj={
                                                                          <AdditionalCovarageCascoMessage
                                                                              isSmartDrive={isSmartDrive(tarrificationProgram)}/>}/>)}
                                            {quotation.cascoAdditionalCoverage.covered === true &&
                                            quotation.vtplAdditionalCoverage.covered === true && (
                                                <span className={`info-message-grey`}>{", "}</span>)
                                            }
                                            {quotation.vtplAdditionalCoverage.covered === true && (
                                                <OutputValueFieldInfo id={'AdditionalCovarageVtpl'}
                                                                      name={'AdditionalCovarageVtpl'}
                                                                      outputValue={strings.additionalCoverageVtpl}
                                                                      messageInfoObj={
                                                                          <AdditionalCovarageVtplMessage/>}/>)}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="verification-form__info__insured">
                                <div style={{gridArea: "title"}}>
                                    <h1>{strings.insured}</h1>
                                </div>

                                <div style={{gridArea: "name"}}>
                                    <OutputValue name={'primaryInsuredFullName'}
                                                 label={strings.insuredFullName}/>
                                </div>

                                <div style={{gridArea: "ipn"}}>
                                    <OutputValue name={'quotation.mtplContract.primaryInsured.ipn'}
                                                 label={strings.insuredIpn}/>
                                </div>

                                <div style={{gridArea: "birth-date"}}>
                                    <OutputValue name={'quotation.mtplContract.primaryInsured.birthDate'}
                                                 outputValue={dateFormat(quotation.mtplContract.primaryInsured.birthDate)}
                                                 label={strings.insuredBirthDate}/>
                                </div>

                                {isQuotationEditable &&
                                <div style={{gridArea: "edit"}} className="verification-form__info__insured__edit">
                                    <OutputLink label={strings.edit}
                                                id={'verificationForm-insured-btnEdit'}
                                                icon={<FontAwesomeIcon icon={faPen as any}/>}
                                                onClickListener={() => onBackStepClick(quotation)}/>
                                </div>
                                }

                                <div style={{gridArea: "phone"}}>
                                    <OutputValue name={'quotation.mtplContract.primaryInsured.mobileNumber'}
                                                 label={strings.phoneWithDots}/>
                                </div>

                                <div style={{gridArea: "email"}}>
                                    <OutputValue name={'quotation.mtplContract.primaryInsured.email'}
                                                 label={strings.insuredEmail}/>
                                </div>

                                <div style={{gridArea: "register-city"}}>
                                    <OutputValue name={'quotation.mtplContract.vehicle.registerCity.name'}
                                                 label={strings.insuredRegisterCity}/>
                                </div>
                            </div>

                            <div className="verification-form__info__vehicle">
                                <div style={{gridArea: "title"}}>
                                    <h1>{strings.vehicle}</h1>
                                </div>

                                <div style={{gridArea: "brand"}}>
                                    <OutputValue name={'quotation.mtplContract.vehicle.brand.name'}
                                                 label={`${strings.brand}:`}/>
                                </div>


                                <div style={{gridArea: "model"}}>
                                    <OutputValue name={'quotation.mtplContract.vehicle.model.name'}
                                                 label={`${strings.model}:`}/>
                                </div>

                                {isQuotationEditable &&
                                <div style={{gridArea: "edit"}} className="verification-form__info__vehicle__edit">
                                    <OutputLink label={strings.edit}
                                                id={'verificationForm-vehicle-btnEdit'}
                                                icon={<FontAwesomeIcon icon={faPen as any}/>}
                                                onClickListener={() => onBackStepClick(quotation)}/>
                                </div>
                                }

                                <div style={{gridArea: "plate-number"}}>
                                    <OutputValue name={'quotation.mtplContract.vehicle.plateNumber'}
                                                 label={strings.vehiclePlateNumber2}/>
                                </div>

                                <div style={{gridArea: "vin"}}>
                                    <OutputValue name={'quotation.mtplContract.vehicle.vin'}
                                                 label={strings.vehicleVin}/>
                                </div>

                                {showNewContent &&
                                    (<>
                                        {quotation.mtplContract.vehicle.engineType && <div style={{ gridArea: "engineType" }}>
                                            <OutputValue name={'quotation.mtplContract.vehicle.engineType'}
                                                         outputValue={formatVehicleEngineType(quotation.mtplContract.vehicle.engineType)}
                                                         label={strings.engineType + ':'} />
                                        </div>}
                                        {quotation.mtplContract.vehicle.engineCapacity && <div style={{ gridArea: "engineCapacity" }}>
                                            <OutputValue name={'quotation.mtplContract.vehicle.engineCapacity'}
                                                label={strings.engineCapacity + ':'} />
                                        </div>}
                                        {quotation.mtplContract.vehicle.batteryCapacity && <div style={{ gridArea: "batteryCapacity" }}>
                                            <OutputValue name={'quotation.mtplContract.vehicle.batteryCapacity'}
                                                label={strings.batteryCapacity + ':'} />
                                        </div>}
                                        <div style={{ gridArea: "totalWeight" }}>
                                            <OutputValue name={'quotation.mtplContract.vehicle.totalWeight'}
                                                label={strings.totalWeight + ':'} />
                                        </div>
                                        <div style={{ gridArea: "ownWeight" }}>
                                            <OutputValue name={'quotation.mtplContract.vehicle.ownWeight'}
                                                label={strings.ownWeight + ':'} />
                                        </div>
                                        <div style={{ gridArea: "numberSeating" }}>
                                            <OutputValue name={'quotation.mtplContract.vehicle.numberSeating'}
                                                label={strings.numberSeating + ':'} />
                                        </div>
                                    </>)
                                }
                            </div>
                        </form>
                    )}
                </Formik>
            </div>
            <div className="verification-form__verify">
                <VerificationComponent onVerify={onVerify} title={strings.contractMtplMtsbu}/>
            </div>
        </div>
    )
};
